import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Room } from 'livekit-client';

/**
 * Standard LiveKit text-stream topics.
 *  - lk.chat          → chat messages (we send the student's typed text here)
 *  - lk.transcription → STT/agent transcriptions (the tutor's spoken replies
 *                       arrive here as text because the agent enables
 *                       text_output; see pencil-agents/ai-tutor/main.py)
 * Docs: https://docs.livekit.io/agents/multimodality/text/
 *       https://docs.livekit.io/home/client/data/text-streams/
 */
export const LK_CHAT_TOPIC = 'lk.chat';
export const LK_TRANSCRIPTION_TOPIC = 'lk.transcription';

/**
 * A single text-chat/transcription message. Streaming segments are accumulated
 * in place (keyed by the text-stream `id`) and flipped to `final` once the
 * stream closes — so the UI can render partial text as it arrives and settle on
 * the complete message.
 */
export interface AgentChatMessage {
  /** Text-stream id — stable across the chunks of one message/segment. */
  id: string;
  /** Sender participant identity. */
  identity: string;
  /** Sender is the AI tutor agent (LiveKit `participant.isAgent`). */
  fromAgent: boolean;
  /** Sender is the local user (our own outgoing chat). */
  fromLocal: boolean;
  /** Originating topic (lk.chat | lk.transcription). */
  topic: string;
  /** Accumulated text so far. */
  text: string;
  /** True once the underlying text stream has closed. */
  final: boolean;
  /** Stream creation timestamp (ms) — used for ordering. */
  timestamp: number;
  /** Optional inline image (data URL) — used by local image messages (e.g. the
   * region the student scoped with "Ask about this"). */
  imageUrl?: string;
}

/**
 * FE handler for the LiveKit tutor's text I/O.
 *
 * Receives the agent's text replies (lk.transcription) and any chat messages
 * (lk.chat) via `registerTextStreamHandler`, accumulating streamed chunks into
 * an ordered `messages$` list; and sends the student's typed text to the agent
 * with `localParticipant.sendText(..., { topic: 'lk.chat' })`.
 *
 * The text-stream handlers are registered once on the (singleton) Room object
 * by LivekitService.attach() and persist across connect/disconnect cycles;
 * `clear()` resets the visible history between calls.
 */
@Injectable({ providedIn: 'root' })
export class LivekitAgentChatService {
  private room?: Room;
  /** id → message, so streaming chunks update the same entry in place. */
  private readonly byId = new Map<string, AgentChatMessage>();
  /**
   * Identities we've ever seen as an AI agent (LiveKit `participant.isAgent`).
   * The agent participant disappears from the room when the tutor disconnects, so
   * a later `getParticipantByIdentity` lookup returns nothing — without this we'd
   * lose `fromAgent` and render the raw identity (`agent-…`) instead of "Luna".
   */
  private readonly agentIdentities = new Set<string>();
  private readonly messagesSubject = new BehaviorSubject<AgentChatMessage[]>([]);

  /** Ordered chat + transcription history for the current call. */
  readonly messages$: Observable<AgentChatMessage[]> = this.messagesSubject.asObservable();

  constructor(private readonly zone: NgZone) {}

  /** Register the text-stream handlers on the room. Idempotent per room. */
  attach(room: Room): void {
    if (this.room === room) {
      return;
    }
    this.detach();
    this.room = room;
    this.register(LK_TRANSCRIPTION_TOPIC);
    this.register(LK_CHAT_TOPIC);
  }

  /** Unregister handlers and drop all state (use when the room is torn down). */
  detach(): void {
    const room = this.room;
    if (room) {
      for (const topic of [LK_TRANSCRIPTION_TOPIC, LK_CHAT_TOPIC]) {
        try {
          room.unregisterTextStreamHandler(topic);
        } catch {
          // Handler wasn't registered — nothing to unregister.
        }
      }
    }
    this.room = undefined;
    this.clear();
  }

  /** Reset the visible history (e.g. between calls) without touching handlers. */
  clear(): void {
    this.byId.clear();
    this.agentIdentities.clear();
    this.messagesSubject.next([]);
  }

  /**
   * Send the student's typed message to the tutor over the lk.chat topic. The
   * agent's `text_input_cb` turns it into a reply, which streams back on
   * lk.transcription and shows up in `messages$`.
   *
   * `echo: false` skips the local copy, for a message already shown with
   * {@link addLocalText} while it waited for Luna to join.
   */
  async send(text: string, options: { echo?: boolean } = {}): Promise<void> {
    const trimmed = text.trim();
    const room = this.room;
    if (!trimmed || !room) {
      return;
    }
    const info = await room.localParticipant.sendText(trimmed, { topic: LK_CHAT_TOPIC });
    if (options.echo === false) {
      return;
    }
    // We don't receive our own text streams, so echo the sent message into the
    // history locally (keyed by the stream id) for the chat UI to render.
    this.upsert(
      LK_CHAT_TOPIC,
      info.id,
      room.localParticipant.identity,
      info.timestamp || Date.now(),
      trimmed,
      true,
    );
  }

  private register(topic: string): void {
    const room = this.room;
    if (!room) {
      return;
    }
    try {
      room.registerTextStreamHandler(topic, (reader, participant) => {
        const attrs = reader.info.attributes ?? {};
        // Transcriptions stream interim results as SEPARATE text streams that
        // share an `lk.segment_id` — collapse them onto a single message so a
        // growing utterance updates in place instead of stacking. Chat messages
        // have no segment id, so they fall back to the (unique) stream id.
        const id = attrs['lk.segment_id'] || reader.info.id;
        const identity = participant.identity;
        const timestamp = reader.info.timestamp || Date.now();
        const finalAttr = attrs['lk.transcription_final'];
        // Don't await inside the handler — accumulate chunks in the background.
        void (async () => {
          let text = '';
          try {
            for await (const chunk of reader) {
              text += chunk;
              this.upsert(topic, id, identity, timestamp, text, false);
            }
          } catch (e) {
            console.warn('[livekit-agent-chat] text stream error', e);
          }
          // Chat: final once the stream closes. Transcription: final only when
          // the segment is marked so (interim results carry final="false").
          const final = finalAttr === undefined ? true : finalAttr === 'true';
          this.upsert(topic, id, identity, timestamp, text, final);
        })();
      });
    } catch (e) {
      // registerTextStreamHandler throws if a handler already exists for the topic.
      console.warn(`[livekit-agent-chat] could not register handler for "${topic}"`, e);
    }
  }

  /**
   * Show a student message in the history before it is sent: one typed while Luna is still
   * joining, which goes out with `send(text, { echo: false })` once she is in the room.
   */
  addLocalText(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) {
      return;
    }
    this.zone.run(() => {
      const timestamp = Date.now();
      const id = `local-text-${timestamp}-${this.byId.size}`;
      this.byId.set(id, {
        id,
        identity: 'local',
        fromAgent: false,
        fromLocal: true,
        topic: LK_CHAT_TOPIC,
        text: trimmed,
        final: true,
        timestamp,
      });
      this.emit();
    });
  }

  /**
   * Add a local student-side message carrying an image — the region the student
   * scoped with "Ask about this" — so they can SEE exactly what they asked about.
   * The text ask is still sent over the wire via {@link send}; this is the visual echo.
   */
  addLocalImage(imageUrl: string, text = ''): void {
    this.zone.run(() => {
      const timestamp = Date.now();
      const id = `local-img-${timestamp}-${this.byId.size}`;
      this.byId.set(id, {
        id,
        identity: 'local',
        fromAgent: false,
        fromLocal: true,
        topic: 'local',
        text,
        final: true,
        timestamp,
        imageUrl,
      });
      this.emit();
    });
  }

  private upsert(
    topic: string,
    id: string,
    identity: string,
    timestamp: number,
    text: string,
    final: boolean,
  ): void {
    // Room events fire outside Angular's zone — re-enter so async-pipe consumers
    // (OnPush) re-render.
    this.zone.run(() => {
      const room = this.room;
      const participant = room?.getParticipantByIdentity(identity);
      if (participant?.isAgent) {
        this.agentIdentities.add(identity);
      }
      // Preserve the first-seen timestamp for a given id (kept as a record; message
      // ORDER comes from Map insertion order in emit(), not from this value).
      const existing = this.byId.get(id);
      // Resolve "is this the AI tutor?" robustly so the sender always renders as
      // "Luna" — even after the tutor disconnects (live lookup then returns
      // nothing). Live flag → identities we've seen as agents → the LiveKit
      // `agent-` identity convention → sticky from the existing message.
      const fromAgent =
        Boolean(participant?.isAgent) ||
        this.agentIdentities.has(identity) ||
        identity.startsWith('agent-') ||
        Boolean(existing?.fromAgent);
      const fromLocal = !!room && identity === room.localParticipant.identity;
      this.byId.set(id, {
        id,
        identity,
        fromAgent,
        fromLocal,
        topic,
        text,
        final,
        timestamp: existing?.timestamp ?? timestamp,
      });
      this.emit();
    });
  }

  /**
   * Push the current history to subscribers, in FIRST-ARRIVAL order. A JS Map keeps
   * insertion order and re-`set`ting an existing key does not move it, so iterating
   * `byId` yields messages in the order they first appeared — the correct chat order.
   *
   * We deliberately do NOT sort by `timestamp`: chat (`lk.chat`) and transcription
   * (`lk.transcription`) streams are timestamped by different, unsynchronized sources,
   * so sorting on it interleaved Luna's reply above the user's message that prompted it.
   */
  private emit(): void {
    this.messagesSubject.next(Array.from(this.byId.values()));
  }
}
